/**
 * Le paramétrage — une entrée de menu, et les huit référentiels en onglets dedans.
 *
 * *Demande de Chris, 21/09/2026 : « sur écran accueil je voudrais que tu crées un menu
 * paramétrage dans lequel tu pourras mettre regroupements, ressources génériques, articles,
 * paramétrage transport, distance, parc matériel, associations salarié matériel, calendrier. »*
 *
 * ## La troisième fois que cette forme est demandée
 *
 * Le module de stock l'a prise le 15/09/2026, le SAV le 21/09 au matin. Les trois raisons qui
 * les ont faits valent ici, et l'une d'elles est devenue urgente :
 *
 *  - **la barre de menu déborde sous 1440 px** — c'est un défaut mesuré, pas une crainte : la
 *    page défile de côté. Les huit entrées qui descendent ici sont justement celles qui
 *    l'allongeaient sans jamais servir à avancer sur un chantier ;
 *  - **un onglet se lit d'un coup d'œil**, là où huit boutons au bas d'une barre se cherchent ;
 *  - **une barre de dix-neuf entrées occupait tout le premier écran d'un téléphone**.
 *
 * ## Ce qui NE descend pas ici, et pourquoi
 *
 * **Les chantiers** : on les ouvre pour travailler, pas pour paramétrer. **Les membres** : ce
 * sont des comptes et des droits, pas un référentiel métier — et c'est l'écran qu'on veut
 * trouver sans le chercher le jour où quelqu'un ne peut plus se connecter. Chris ne les a
 * nommés ni l'un ni l'autre ; ils restent où ils sont.
 *
 * ## Les trois onglets réservés à l'administrateur
 *
 * Paramétrage transport, Distance et Calendrier gardent **exactement** la barrière qu'ils
 * avaient dans la barre de menu, déplacée d'un cran : un onglet masqué plutôt qu'un bouton
 * masqué. Comme avant, **ce masquage n'est qu'un confort** — la barrière qui compte est côté
 * serveur, et un onglet caché n'empêche personne d'appeler l'API.
 *
 * L'entrée « Paramétrage » du menu, elle, est ouverte à tous : cinq des huit onglets l'étaient,
 * et le parc matériel l'est délibérément (un mécano d'atelier n'est nommé sur aucun chantier,
 * donc tous les écrans qui partent d'un chantier lui sont muets — celui-ci part de la machine
 * qu'il a sous les yeux).
 *
 * ## Le scan d'une étiquette ouvre toujours l'objet scané
 *
 * `/article/<code>` et `/materiel/<code>` menaient à deux écrans du menu ; ces écrans sont
 * maintenant des onglets. Le chemin arrive donc ici **avec son onglet déjà ouvert** — les
 * étiquettes déjà collées sur les machines et dans les rayonnages continuent de fonctionner,
 * ce qui est la condition pour ne pas avoir à les réimprimer.
 */
import { useState } from "react";
import type { JSX } from "react";
import type { Role } from "./api";
import { Articles } from "./Articles";
import { Associations } from "./Associations";
import { Calendrier } from "./Calendrier";
import { Distances } from "./Distances";
import { Groupes } from "./Groupes";
import { Materiel } from "./Materiel";
import { ParametrageTransport } from "./ParametrageTransport";
import { Referentiels } from "./Referentiels";

type Onglet =
  | "groupes"
  | "referentiels"
  | "articles"
  | "transport"
  | "distances"
  | "materiel"
  | "associations"
  | "calendrier";

/**
 * Les onglets, dans l'ordre exact de la demande de Chris.
 *
 * `admin` marque ceux dont le bouton de menu était déjà réservé à l'administrateur.
 */
const ONGLETS: readonly {
  readonly code: Onglet;
  readonly libelle: string;
  readonly admin?: true;
}[] = [
  { code: "groupes", libelle: "Regroupements" },
  { code: "referentiels", libelle: "Ressources génériques" },
  { code: "articles", libelle: "Articles" },
  { code: "transport", libelle: "Paramétrage transport", admin: true },
  { code: "distances", libelle: "Distance", admin: true },
  { code: "materiel", libelle: "Parc matériel" },
  { code: "associations", libelle: "Association Salarié/Matériels" },
  { code: "calendrier", libelle: "Calendrier", admin: true },
];

export function Parametrage({
  role,
  codeArticle,
  codeMateriel,
}: {
  readonly role: Role;
  /** Le code article que l'adresse demande — un QR scané. */
  readonly codeArticle?: string | undefined;
  /** Le code matériel que l'adresse demande — un QR scané. */
  readonly codeMateriel?: string | undefined;
}): JSX.Element {
  /**
   * L'onglet ouvert.
   *
   * Un scan ouvre l'objet scané ; sinon le premier de la liste. Pas de mémorisation du dernier
   * onglet visité : on vient ici pour une chose précise, et rouvrir sur l'écran de la fois
   * d'avant fait chercher où l'on est.
   */
  const [onglet, setOnglet] = useState<Onglet>(
    codeArticle !== undefined ? "articles" : codeMateriel !== undefined ? "materiel" : "groupes",
  );

  const visibles = ONGLETS.filter((o) => o.admin !== true || role === "admin");

  return (
    <section className="parametrage">
      <nav className="stocks__onglets">
        {visibles.map(({ code, libelle }) => (
          <button
            key={code}
            type="button"
            className={onglet === code ? "actif" : undefined}
            onClick={() => {
              setOnglet(code);
            }}
          >
            {libelle}
          </button>
        ))}
      </nav>

      {onglet === "groupes" && <Groupes />}
      {onglet === "referentiels" && <Referentiels />}
      {onglet === "articles" && <Articles codeInitial={codeArticle} />}
      {onglet === "transport" && role === "admin" && <ParametrageTransport />}
      {onglet === "distances" && role === "admin" && <Distances />}
      {onglet === "materiel" && <Materiel codeInitial={codeMateriel} />}
      {onglet === "associations" && <Associations />}
      {onglet === "calendrier" && role === "admin" && <Calendrier />}
    </section>
  );
}
